import { Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { ProcessDefinition, WorkflowApiService } from '../../workflow/workflow-api.service';
import { DynamicEntity } from '../dynamic-api.service';
import {
  FORM_SUBMIT_ACTION_TYPES,
  FormSchemaModel,
  FormSubmitAction,
  FormSubmitActionType,
  fieldLabelOptions,
  readLocalizedText,
  writeLocalizedText
} from '../../../shared/dynamic-form/form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-builder-submit-actions-panel',
  imports: [
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    AinFormFieldComponent,
    InfoHintComponent
  ],
  template: `
    <section class="submit-actions-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.submitActions.infoTitle'"
        [descriptionKey]="'dynamic.builder.submitActions.infoDescription'"
        [bulletKeys]="['dynamic.builder.submitActions.infoBullet']" />

      <div class="toolbar">
        <strong>{{ t('dynamic.builder.submitActions.title') }}</strong>
        @if (canEdit()) {
          <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.submitActions.add')" (click)="addAction()"></button>
        }
      </div>

      @for (action of submitActions(); track $index; let idx = $index) {
        <article class="action-card">
          <div class="action-header">
            <span>{{ t('dynamic.builder.submitActions.order') }} {{ idx + 1 }}</span>
            @if (canEdit()) {
              <div class="action-buttons">
                <button pButton type="button" text icon="pi pi-arrow-up" [disabled]="idx === 0" (click)="moveAction(idx, -1)"></button>
                <button pButton type="button" text icon="pi pi-arrow-down" [disabled]="idx === submitActions().length - 1" (click)="moveAction(idx, 1)"></button>
                <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeAction(idx)"></button>
              </div>
            }
          </div>

          <ain-form-field [label]="t('dynamic.builder.submitActions.type')">
            <p-select
              [options]="actionTypeOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="action.type"
              [disabled]="!canEdit()"
              appendTo="body"
              (ngModelChange)="updateActionType(idx, $event)" />
          </ain-form-field>

          @switch (action.type) {
            @case ('createEntityRecord') {
              <ain-form-field [label]="t('dynamic.builder.submitActions.targetEntity')">
                <p-select
                  [options]="entityOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="action.targetEntityKey ?? ''"
                  [disabled]="!canEdit()"
                  [filter]="true"
                  appendTo="body"
                  (ngModelChange)="updateAction(idx, { targetEntityKey: $event, map: {} })" />
              </ain-form-field>
              @if (action.targetEntityKey) {
                <div class="mapping-block">
                  <strong>{{ t('dynamic.builder.submitActions.fieldMapping') }}</strong>
                  @for (row of mappingRows(action); track $index; let mapIdx = $index) {
                    <div class="mapping-row">
                      <p-select
                        [options]="fieldOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [ngModel]="row.formField"
                        [disabled]="!canEdit()"
                        [filter]="true"
                        appendTo="body"
                        [placeholder]="t('dynamic.builder.submitActions.formField')"
                        (ngModelChange)="updateMapping(idx, mapIdx, $event, row.entityField)" />
                      <p-select
                        [options]="entityFieldOptions(action.targetEntityKey!)"
                        optionLabel="label"
                        optionValue="value"
                        [ngModel]="row.entityField"
                        [disabled]="!canEdit()"
                        [filter]="true"
                        appendTo="body"
                        [placeholder]="t('dynamic.builder.submitActions.entityField')"
                        (ngModelChange)="updateMapping(idx, mapIdx, row.formField, $event)" />
                      @if (canEdit()) {
                        <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeMapping(idx, mapIdx)"></button>
                      }
                    </div>
                  }
                  @if (canEdit()) {
                    <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.submitActions.addMapping')" (click)="addMapping(idx)"></button>
                  }
                </div>
              }
            }
            @case ('startWorkflow') {
              <ain-form-field [label]="t('dynamic.builder.submitActions.process')">
                <p-select
                  [options]="processOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="action.processKey ?? ''"
                  [disabled]="!canEdit()"
                  [filter]="true"
                  [showClear]="true"
                  appendTo="body"
                  (ngModelChange)="updateAction(idx, { processKey: $event || undefined })" />
              </ain-form-field>
            }
            @case ('sendNotification') {
              <ain-form-field [label]="t('dynamic.builder.submitActions.template')">
                <input
                  pInputText
                  [ngModel]="action.template ?? ''"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateAction(idx, { template: $event })" />
              </ain-form-field>
            }
            @case ('showMessage') {
              <ain-form-field [label]="t('dynamic.builder.submitActions.message')">
                <textarea
                  pTextarea
                  rows="2"
                  [ngModel]="messageValue(action)"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateMessage(idx, $event)"></textarea>
              </ain-form-field>
            }
            @case ('redirect') {
              <ain-form-field [label]="t('dynamic.builder.submitActions.url')">
                <input
                  pInputText
                  [ngModel]="action.url ?? ''"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateAction(idx, { url: $event })" />
              </ain-form-field>
            }
          }
        </article>
      } @empty {
        <p class="empty">{{ t('dynamic.builder.submitActions.empty') }}</p>
      }
    </section>
  `,
  styles: [`
    .submit-actions-panel { display: grid; gap: 12px; }
    .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .action-card {
      border: 1px solid var(--surface-border);
      border-radius: 8px;
      padding: 12px;
      display: grid;
      gap: 10px;
    }
    .action-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .action-buttons { display: flex; gap: 4px; }
    .mapping-block { display: grid; gap: 8px; }
    .mapping-row { display: grid; grid-template-columns: 1fr 1fr auto; gap: 8px; align-items: center; }
    .empty { margin: 0; color: var(--text-color-secondary); }
  `]
})
export class FormBuilderSubmitActionsPanelComponent {
  private readonly localization = inject(LocalizationService);
  private readonly editCultureService = inject(FormBuilderEditCultureService);
  private readonly workflowApi = inject(WorkflowApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly schema = input.required<FormSchemaModel>();
  readonly culture = input.required<string>();
  readonly submitActions = input<FormSubmitAction[]>([]);
  readonly entities = input<DynamicEntity[]>([]);
  readonly entityFieldMap = input<Record<string, { label: string; value: string }[]>>({});
  readonly canEdit = input(true);

  readonly submitActionsChange = output<FormSubmitAction[]>();

  readonly processes = signal<ProcessDefinition[]>([]);

  readonly actionTypeOptions = computed(() =>
    FORM_SUBMIT_ACTION_TYPES.map(value => ({
      label: this.t(`dynamic.builder.submitActions.type.${value}`),
      value
    }))
  );

  readonly entityOptions = computed(() =>
    (this.entities() ?? []).map(entity => ({
      label: `${entity.displayName} (${entity.name})`,
      value: entity.id
    }))
  );

  readonly fieldOptions = computed(() => fieldLabelOptions(this.schema(), this.culture()));

  readonly processOptions = computed(() =>
    this.processes().map(process => ({
      label: `${process.name} (v${process.currentVersion})`,
      value: process.id
    }))
  );

  constructor() {
    this.workflowApi.listDefinitions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: items => this.processes.set(items),
        error: () => this.processes.set([])
      });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  entityFieldOptions(entityId: string): { label: string; value: string }[] {
    return this.entityFieldMap()[entityId] ?? [];
  }

  messageValue(action: FormSubmitAction): string {
    return readLocalizedText(action.message, this.editCultureService.editCulture());
  }

  mappingRows(action: FormSubmitAction): { formField: string; entityField: string }[] {
    const map = action.map ?? {};
    const rows = Object.entries(map).map(([formField, entityField]) => ({ formField, entityField }));
    return rows.length ? rows : [{ formField: '', entityField: '' }];
  }

  addAction(): void {
    this.submitActionsChange.emit([...(this.submitActions() ?? []), { type: 'showMessage', message: {} }]);
  }

  removeAction(index: number): void {
    this.submitActionsChange.emit((this.submitActions() ?? []).filter((_, idx) => idx !== index));
  }

  moveAction(index: number, delta: number): void {
    const next = [...(this.submitActions() ?? [])];
    const target = index + delta;
    if (target < 0 || target >= next.length) {
      return;
    }
    [next[index], next[target]] = [next[target], next[index]];
    this.submitActionsChange.emit(next);
  }

  updateActionType(index: number, type: FormSubmitActionType): void {
    const next = [...(this.submitActions() ?? [])];
    next[index] = { type };
    this.submitActionsChange.emit(next);
  }

  updateAction(index: number, patch: Partial<FormSubmitAction>): void {
    const next = [...(this.submitActions() ?? [])];
    next[index] = { ...next[index], ...patch };
    this.submitActionsChange.emit(next);
  }

  updateMessage(index: number, value: string): void {
    const next = [...(this.submitActions() ?? [])];
    next[index] = {
      ...next[index],
      type: 'showMessage',
      message: writeLocalizedText(next[index].message, this.editCultureService.editCulture(), value)
    };
    this.submitActionsChange.emit(next);
  }

  addMapping(index: number): void {
    const action = (this.submitActions() ?? [])[index];
    const map = { ...(action.map ?? {}), '': '' };
    this.updateAction(index, { map });
  }

  updateMapping(index: number, mapIndex: number, formField: string, entityField: string): void {
    const action = (this.submitActions() ?? [])[index];
    const rows = this.mappingRows(action);
    rows[mapIndex] = { formField, entityField };
    const map: Record<string, string> = {};
    for (const row of rows) {
      if (row.formField.trim()) {
        map[row.formField.trim()] = row.entityField;
      }
    }
    this.updateAction(index, { map });
  }

  removeMapping(index: number, mapIndex: number): void {
    const action = (this.submitActions() ?? [])[index];
    const rows = this.mappingRows(action).filter((_, idx) => idx !== mapIndex);
    const map: Record<string, string> = {};
    for (const row of rows) {
      if (row.formField.trim()) {
        map[row.formField.trim()] = row.entityField;
      }
    }
    this.updateAction(index, { map: Object.keys(map).length ? map : {} });
  }
}
